import React from "react";

import type { CoaOption, CoaSelectState } from "./CoaSelectPrompt";
import { coaStateBlocksContinue } from "./CoaSelectPrompt";
import { BASE_COA_ID, deriveCoaState } from "./deriveCoaState";

export type UseCoaSelectionParams = {
    isOperatingStatement: boolean;
    baselineStatus: "pending" | "rejected" | "resolved";
    coaLocked: boolean;
    projectCoaId: number | null;
    projectDealName: string | null;
    projectDescription: string | null;
    availableCoAs: CoaOption[];
    availableCoAsStatus: "pending" | "rejected" | "resolved";
    retryCoaData: () => void;
    projectId: number;
    databaseId: number;
    assignCoa: (params: {
        projectId: number;
        databaseId: number;
        coaId: number;
        dealName: string;
        description: string | null | undefined;
    }) => Promise<number | undefined>;
};

export type UseCoaSelectionResult = {
    coaState: CoaSelectState;
    coaBlocksContinue: boolean;
    /**
     * Assign the selected CoA (PUT /project/update) if the prompt applies and
     * the selection differs from the project's current one, then invoke `run`
     * with the project id the extraction should target — the backend may
     * re-mint it. Resolves to whether `run` was invoked: it is NOT when the
     * assign fails (its own error toast fires, and the CoA data is refetched so
     * a lock that appeared since the last check makes the prompt disappear
     * instead of inviting doomed retries) or when an assign is already in
     * flight (re-entrancy guard against double-clicks).
     */
    assignCoaThenRun: (
        run: (effectiveProjectId: number) => void,
    ) => Promise<boolean>;
};

/**
 * Owns the CoA selection for an Operating Statement upload: which CoA is
 * picked, how it is seeded (a sole permitted CoA is selected silently), when it
 * resets, and the assign-before-extract step. Shared by every upload surface so
 * they can't drift apart.
 */
export const useCoaSelection = (
    params: UseCoaSelectionParams,
): UseCoaSelectionResult => {
    const {
        isOperatingStatement,
        baselineStatus,
        coaLocked,
        projectCoaId,
        projectDealName,
        projectDescription,
        availableCoAs,
        availableCoAsStatus,
        retryCoaData,
        projectId,
        databaseId,
        assignCoa,
    } = params;

    // Only a Base-CoA project needs a CoA chosen — any other value was a
    // positive user choice at creation.
    const coaSelectionNeeded = projectCoaId === BASE_COA_ID;

    const [selectedCoaId, setSelectedCoaId] = React.useState<number | null>(
        null,
    );
    // Clear the selection when the target project changes (the same surface
    // must never carry one project's CoA choice to another).
    React.useEffect(() => {
        setSelectedCoaId(null);
    }, [projectId, databaseId]);
    // Seed / re-sync the selection once the permitted-CoA list resolves: a sole
    // option is selected silently (assigned without a prompt), otherwise keep a
    // still-valid selection and force an explicit choice when there is none.
    React.useEffect(() => {
        if (
            !isOperatingStatement ||
            baselineStatus !== "resolved" ||
            !coaSelectionNeeded ||
            coaLocked ||
            availableCoAsStatus !== "resolved"
        ) {
            return;
        }
        setSelectedCoaId(prev => {
            if (availableCoAs.length === 1) {
                return availableCoAs[0]?.coaId ?? null;
            }
            return prev !== null &&
                availableCoAs.some(coa => coa.coaId === prev)
                ? prev
                : null;
        });
    }, [
        isOperatingStatement,
        baselineStatus,
        coaSelectionNeeded,
        coaLocked,
        availableCoAsStatus,
        availableCoAs,
    ]);

    // Guards against double-submission: the ref gives a synchronous re-entrancy
    // check; the state disables Continue (via coaState.assigning) while the
    // assign PUT is in flight.
    const assignInFlightRef = React.useRef(false);
    const [isAssigning, setIsAssigning] = React.useState(false);

    const coaState = deriveCoaState({
        isOperatingStatement,
        baselineStatus,
        coaSelectionNeeded,
        coaLocked,
        availableCoAsStatus,
        availableCoAs,
        selectedCoaId,
        assigning: isAssigning,
        onSelect: setSelectedCoaId,
        onRetry: retryCoaData,
    });

    const coaStateKind = coaState.kind;
    const assignCoaIfNeeded = React.useCallback(async (): Promise<
        number | null
    > => {
        if (assignInFlightRef.current) return null;
        // No prompt (already-assigned project, locked CoA, rent roll) — nothing
        // to assign, the extraction targets the project as-is.
        if (coaStateKind !== "ready") return projectId;
        if (selectedCoaId === null || projectDealName === null) return null;
        if (selectedCoaId === projectCoaId) return projectId;
        assignInFlightRef.current = true;
        setIsAssigning(true);
        try {
            const newProjectId = await assignCoa({
                projectId,
                databaseId,
                coaId: selectedCoaId,
                dealName: projectDealName,
                description: projectDescription,
            });
            return newProjectId ?? projectId;
        } catch {
            // Self-heal (mirrors core-ui OS-3983): the usual cause of a
            // rejected assign is a CoA that got locked since the last check —
            // refetch so the prompt disappears rather than 409ing on every
            // retry. The mutation's own error toast has already fired.
            retryCoaData();
            return null;
        } finally {
            assignInFlightRef.current = false;
            setIsAssigning(false);
        }
    }, [
        assignCoa,
        coaStateKind,
        selectedCoaId,
        projectCoaId,
        projectDealName,
        projectDescription,
        projectId,
        databaseId,
        retryCoaData,
    ]);

    const assignCoaThenRun = React.useCallback(
        async (run: (effectiveProjectId: number) => void) => {
            const effectiveProjectId = await assignCoaIfNeeded();
            if (effectiveProjectId === null) return false;
            run(effectiveProjectId);
            return true;
        },
        [assignCoaIfNeeded],
    );

    return {
        coaState,
        coaBlocksContinue: coaStateBlocksContinue(coaState),
        assignCoaThenRun,
    };
};
